<template>
  <div v-if="needRecommendedProduct" :class="$style.wrapper">
    <div ref="scriptContainer" />
    <div v-if="recommendations && recommendations.length > 0">
      <aw-sf-product-carousel
        v-for="(recommendation, index) in recommendations"
        :key="recommendation.name"
        :title="index === 0 ? $awt(`aw.sf_recommendation.${page}.title`) : recommendation.title"
        :placeholder-text="$awt(`aw.sf_recommendation.${page}.placeholder`)"
        :products="getProducts(recommendation)"
        :bordered="bordered"
        :analytics-banner-interaction="extendedAnalyticsBannerInteractions[index]"
        :analytics-measurable="analyticsMeasurable"
      />
    </div>
    <aw-empty v-else>
      <span v-text="$awt(`aw.cms.recommendation_carousel.fallback.text`)" />
    </aw-empty>
  </div>
</template>

<script setup>
  import { computed, nextTick, onMounted, onUnmounted, provide, ref } from 'vue';
  import { useNuxtApp, useRuntimeConfig } from 'nuxt/app';
  import { useAwSfRecommendationStore } from '~~/common/stores/awSfRecommendation';
  import AwSfProductCarousel from '~~/common/components/Common/AwSfProductCarousel.vue';
  import { crossPlatformHashNotExceptionSafe } from '~~/common/utils/index';
  import { HASH_SHA_256 } from '~~/common/config/CrossPlatformHashFunction';
  import { AW_SF_RECOMMENDATION } from '~~/common/config/RecommendationConfig';
  import { useUserStore } from '~~/common/stores/user';
  import AwEmpty from '~~/common/components/Common/AwEmpty.vue';
  import analyticsBannerInteractionMixin from '~~/common/mixins/analyticsBannerInteractionMixin';

  const {
    $awt,
    $logger,
  } = useNuxtApp();

  const runtimeConfig = useRuntimeConfig().public;

  const awSfRecommendationStore = useAwSfRecommendationStore();
  const userStore = useUserStore();
  const recommendations = computed(() => awSfRecommendationStore.recommendations);
  const userId = computed(() => userStore.data?.personalInfo?.cdEncodedExternalId ?? 'anon');

  provide('isCartPage', false);

  const props = defineProps({
    ...analyticsBannerInteractionMixin.props,
    page: {
      type: String,
      required: false,
      default: 'product',
    },
    forItems: {
      type: String,
      default: '',
    },
    bordered: {
      type: Boolean,
      default: false,
    },
  });

  const scriptContainer = ref(null);

  const needRecommendedProduct = computed(() => {
    return runtimeConfig.SfmcMid && runtimeConfig.isSfmcEnabled && runtimeConfig.isSfmcRecoEnabled;
  });

  const itemParams = computed(() => {
    switch (props.page) {
      case 'cart':
        return `&cart=${props.forItems}`;
      case 'product':
        return `&item=${props.forItems}`;
      case 'category':
        return `&category=${encodeURIComponent(props.forItems)}`;
      case 'search':
        return `&search=${encodeURIComponent(props.forItems)}`;
      case 'home':
      default:
        return '';
    }
  });

  const script = computed(() => {
    // eslint-disable-next-line
  return `<script src="https://${runtimeConfig.SfmcMid}.recs.igodigital.com/a/v2/${runtimeConfig.SfmcMid}/${props.page}/recommend.json?callback=useNuxtApp().$awSFMCClient.getRecommendation${itemParams.value}" type="text/javascript"><\/script>`
  });

  const extendedAnalyticsBannerInteractions = computed(() => {
    return recommendations.value?.map((recommendation, index) => {
      const title = index === 0 ? $awt(`aw.sf_recommendation.${props.page}.title`) : recommendation.title;
      return {
        ...props.analyticsBannerInteraction,
        promotionName: props.analyticsBannerInteraction?.promotionName ?? title,
      };
    });
  });


  onMounted(() => {
    nextTick(async () => {
      if (!needRecommendedProduct.value) {
        return;
      }

      const {
        value: sha256ItemParam,
        error: hashingErr,
      } = await crossPlatformHashNotExceptionSafe({
        hashFunction: HASH_SHA_256,
        hashableStr: `${userId.value}_${itemParams.value}`,
      });
      if (!sha256ItemParam && hashingErr) {
        $logger.error('Error while hashing item params:', hashingErr);
        return;
      }

      const sfmcRecommendation = JSON.parse(localStorage.getItem(AW_SF_RECOMMENDATION)) || {};
      const currentElement = sfmcRecommendation[sha256ItemParam];
      if (currentElement) {
        const now = new Date().getTime();

        if (currentElement.validTo > now) {
          awSfRecommendationStore.setCacheKey(sha256ItemParam);
          await awSfRecommendationStore.fetchSFRecommendation([currentElement.recommendation]);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete sfmcRecommendation[sha256ItemParam];
          localStorage.setItem(AW_SF_RECOMMENDATION, JSON.stringify(sfmcRecommendation));
          fetchSFRecommendation(sha256ItemParam);
        }
      } else {
        fetchSFRecommendation(sha256ItemParam);
      }
    });
  });

  function fetchSFRecommendation (cacheKey) {
    awSfRecommendationStore.setCacheKey(cacheKey);
    try {
      if (scriptContainer.value) {
        const postscribe = () => import('postscribe');

        postscribe().then((pscribe) => {
          pscribe.default(null, null, 'clear');
          if (scriptContainer.value) {
            pscribe.default(scriptContainer.value, script.value);
          }
        });
      }
    } catch (error) {
      this.$logger.error(error);
    }
  }

  onUnmounted(() => {
    awSfRecommendationStore.clearRecommendation();
  });

  function getProducts (recommendation) {
    return recommendation?.products?.map((product) => {
      return {
        ...product,
        ...(recommendation.items.find((item) => {
          return item.product_code === product.selectedVariant.sku;
        })),
      };
    }) ?? [];
  }

</script>

<style module lang="scss" rel="stylesheet/scss">
.wrapper {
  margin-bottom: 32px;

  .carousel {
    margin-top: 24px;
  }
}

.tabCarouselPlaceHolder {
  margin-top: 2px;
}
</style>
